import React, { useContext, useEffect, useState } from "react";
import { IToastContext, ToastContext } from "../../../providers/Toast";
import Button from "../../../components/shared/button/Button";
import { useTranslation } from "react-i18next";
import { ROUTES } from "../../../routing/routes";
import queryString from "query-string";
import firebase from "./../../../firebase/firebase";
import * as Yup from "yup";
import CustomInputPassword from "../../../components/shared/forms/input/CustomInputPassword";
import { useFormik } from "formik";
import { Link, useHistory } from "react-router-dom";
import { signOut, updatePassword } from "../../../context/auth/action";
import { useDispatch } from "react-redux";
import _environment from "../../../_environment/_environment";

// -----------------------------------------------------------------
// I n t e r f a c e s
// -----------------------------------------------------------------
interface Props {
}

const AuthActionView = ({ ...restProps }: Props) => {
  // -----------------------------------------------------------------
  // L o c a l   v a r s
  // -----------------------------------------------------------------

  const dispatch = useDispatch();

  // Access to i18n instance that you may use to translate your content.
  const { t } = useTranslation();

  // Access to the history instance that you may use to navigate.
  let history = useHistory();

  // Access to toast manager hook that you may use to show toasts.
  const { openToast } = useContext<IToastContext>(ToastContext);

  // -----------------------------------------------------------------
  // N a v i g a t i o n   v a r s
  // -----------------------------------------------------------------

  // -----------------------------------------------------------------
  // S t a t e
  // -----------------------------------------------------------------
  // To track invalid requests
  const [invalid, setInvalid] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  // Action code linked to the account
  const [actionCode, setActionCode] = useState<string>("");

  // Email account to reset password
  const [email, setEmail] = useState<string | undefined>(undefined);

  // Variables to check if email is verified
  const [verifying, setVerifying] = useState<boolean>(false);
  const [verified, setVerified] = useState<boolean>(false);

  // Variables to track password reset request
  const [loading, setLoading] = useState<boolean>(false);
  const [completed, setCompleted] = useState<boolean>(false);

  // -----------------------------------------------------------------
  // R e f s  (DOM)
  // -----------------------------------------------------------------

  // -----------------------------------------------------------------
  // u s e S e l e c t o r   m e t h o d s  (redux)
  // -----------------------------------------------------------------

  // -----------------------------------------------------------------
  // W o r k i n g   m e t h o d s
  // -----------------------------------------------------------------
  const handleResetPassword = async (actionCode?: any) => {
    setVerifying(true);
    try {
      // Verify the code is valid
      const email = await firebase.auth().verifyPasswordResetCode(actionCode);

      setEmail(email);
      setVerified(true);
      setActionCode(actionCode);
    } catch (error) {
      // Invalid or expired action code. Ask user to try to reset the password
      // again.
      console.error(error);

      openToast({
        type: "error",
        title: "errors.general.title",
        message: "errors.password_reset.expired_code",
        callback: () => {
        },
      });

      setInvalid(true);
      setError(t("errors.password_reset.expired_code"));
    } finally {
      setVerifying(false);
    }
  };

  const saveNewPassword = async (password: string) => {
    setLoading(true);
    try {
      // Verify the code is valid
      const response = await firebase.auth().confirmPasswordReset(actionCode, password);

      // Save password on firestore
      await updatePassword(email, password);

      console.log(response);

      openToast({
        type: "success",
        title: "success.general.title",
        message: "success.password_reset",
        callback: () => {
        },
      });

      setCompleted(true);
    } catch (error) {
      // Error occurred during confirmation. The code might have expired or the
      // password is too weak.
      console.error(error);

      openToast({
        type: "error",
        title: "errors.general.title",
        message: error.message,
        callback: () => {
        },
      });

      setCompleted(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (data: any) => {
    if (!loading) {
      return formik.handleSubmit(data);
    }
  };

  // -----------------------------------------------------------------
  // f o r m
  // -----------------------------------------------------------------
  // Formik Initialisation data
  const formik = useFormik({
    initialValues: {
      password: "",
    },
    validationSchema: Yup.object().shape({
      password: Yup.string()
        .required("Password is required!")
        .matches(
          /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?([#?!@$%^&*-]|[]]|[[]|[_])).{10,}$/,
          "Must contain at least 10 Characters, one uppercase, one lowercase, one number and one special case character",
        ),
    }),
    onSubmit: (values: any) => {
      saveNewPassword(values.password);
    },
  });

  // -----------------------------------------------------------------
  // R e n d e r   m e t h o d s
  // -----------------------------------------------------------------
  const renderForm = (): React.ReactNode => {
    return (
      <>
        <form className="user">
          <div className="form-group">
            <CustomInputPassword
              id="password"
              name="password"
              label={t("forms.password")}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.password}
              errors={formik.errors.password}
              touched={formik.touched.password}
            />
          </div>
          <Button
            loading={loading}
            disabled={loading}
            onClick={handleSubmit}
            submit={true}
            className="btn btn-primary btn-block mt-3"
          >
            {t("forms.submit")}
          </Button>
        </form>
      </>
    );
  };

  // -----------------------------------------------------------------
  // L i f e c y c l e
  // -----------------------------------------------------------------
  /**
   * This method is called the first time the component is mounted
   * @function
   */
  const init = (): void => {
    console.log("[AuthActionView] init");

    // Change view based on params
    const params = queryString.parse(history.location.search);

    // If confirm email, navigate to that view
    if (params.mode === "verifyEmail") {
      // Force logout, not passing history param so no redirect to signIn
      dispatch(signOut());

      history.push({
        pathname: ROUTES.CONFIRM_EMAIL,
        search: history.location.search,
      });
      return;
    }

    if (params.mode === "resetPassword") {
      handleResetPassword(params.oobCode);
      return;
    }

    setInvalid(true);
    return;
  };

  /**
   * This method is called when the component is unmounted
   * @function
   */
  const destroy = (): void => {
    console.log("[AuthActionView] destroy");
    // destroy all
  };

  // -----------------------------------------------------------------
  // u s e E f f e c t   m e t h o d s
  // -----------------------------------------------------------------
  /**
   * This hook is called once when the component is mounted
   */
  useEffect(() => {
    init();
    return () => {
      destroy();
    };
  }, []);

  // -----------------------------------------------------------------
  // T e m p l a t e
  // -----------------------------------------------------------------
  return (
    <div className="main-content">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-xl-6 col-lg-7 col-md-9">
            <div className="box o-hidden my-5">
              <div className="logo-vertical-wrapper">
                <a href={_environment.baseURL}>
                  <img src={_environment.assets.logo} alt={t("title")}/>
                </a>
              </div>
              <div className="card-body p-0">
                <div className="p-4">
                  {/* Invalid request */}
                  {invalid && (
                    <>
                      {!!error && (
                        <>
                          <p className="mb-4">{error}</p>
                          <a href={ROUTES.FORGOT_PASSWORD} className="btn btn-primary btn-block">
                            {t("forms.try_again")}
                          </a>
                        </>
                      )}

                      <hr/>
                      <div className="text-center">
                        <Link className="link-label" to={ROUTES.SIGN_IN}>{t("forms.back_sign_in")}</Link>
                      </div>
                    </>
                  )}

                  {/* Is is verifying */}
                  {!verified && verifying && (
                    <>
                      <div className="text-center">
                        <h1 className="h4 text-gray-900 mb-4">We are verifying you...</h1>
                      </div>
                      <p className="description mb-4 text-center">
                        <i className="fas fa-spinner fa-spin"></i>
                      </p>
                    </>
                  )}

                  {/* If verified */}
                  {!completed && verified && !verifying && (
                    <>
                      <div className="text-center">
                        <h1 className="h4 text-gray-900 mb-4">
                          Cool! Your reset password link is valid!
                        </h1>
                      </div>
                      <p className="description mb-4">
                        Insert the new password for the account <strong>{email}</strong>
                      </p>

                      {renderForm()}
                    </>
                  )}

                  {/* If completed */}
                  {!loading && completed && (
                    <>
                      <div className="text-center">
                        <h1 className="h4 text-gray-900 mb-4">
                          Cool! Password changed successfully!
                        </h1>
                      </div>
                      <p className="description mb-4">
                        Now you can go back to sign in page and enter with your new credentials!
                      </p>

                      <a href={ROUTES.SIGN_IN} className="btn btn-primary btn-block">
                        {t("forms.back_sign_in")}
                      </a>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default AuthActionView;
